package com.evoapps.evoshift.domain.market.payroll.poland

import com.evoapps.evoshift.domain.market.payroll.LeaveDay
import com.evoapps.evoshift.domain.market.payroll.LeaveKind
import com.evoapps.evoshift.domain.market.payroll.MonthPayFactors
import com.evoapps.evoshift.domain.market.payroll.MonthWorkInput
import com.evoapps.evoshift.domain.market.payroll.WorkedSpan
import com.evoapps.evoshift.domain.model.CompensationModel
import com.evoapps.evoshift.domain.model.RatePeriod
import com.evoapps.evoshift.domain.model.SystemContractType
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.plus
import kotlinx.datetime.toInstant
import kotlin.math.ceil
import kotlin.math.max
import kotlin.math.min

/**
 * SH-57: what a Polish month pays for time not worked, and for pay terms that change during it,
 * as an estimate (the accountant's payroll decides; sources in [PolandRates]).
 *
 * - **Pay terms by date**: each day is paid with the terms in force that day. A salary that changes
 *   during the month is paid pro rata to the norm's working hours before and after the change
 *   (as for a part of a month, SH-48); hourly shifts are priced on their own date.
 * - **Sick leave** on an employment contract: the employer pays 80% (100% when the sick note says
 *   so) of the base for each calendar day, for the first 33 days of illness in the calendar year
 *   (14 for people over 50), then ZUS pays the sickness benefit at the same rate. The base is the
 *   average monthly pay of the up to 12 months before in which the person worked here, less the
 *   employee's social contributions (13.71%), over 30 days. A salary is reduced by 1/30 for each
 *   day of illness. On a civil-law contract with the voluntary sickness insurance, ZUS pays the
 *   benefit from the first day. Neither carries social contributions.
 * - **Annual leave** on an employment contract: each leave day on a working day is 8 hours (times
 *   the FTE); hourly pay pays them at the day's rate, a salary already does. The variable pay of
 *   the 3 months before (night allowance, overtime) is added per leave hour. The entitlement is 20
 *   or 26 days a year pro rata to the FTE.
 * - Leave days shorten the working-time norm of the settlement period by their hours.
 *
 * Simplified on purpose: the averages use the pay worked out here (shifts and pay terms), not
 * what was actually paid; months the person did not work here are left out rather than filled up;
 * the 90-day waiting period of the sickness insurance and the 70% for hospital stays are ignored.
 */
internal object PolishLeavePay {

    /** A stretch of the month with one set of pay terms. */
    data class RatePart(val from: LocalDate, val to: LocalDate, val compensation: CompensationModel)

    /** What leave changes in the working-time maths of the month. */
    data class LeaveEffect(
        /** Leave working days in the settlement period up to the end of the month (shorten its norm). */
        val periodLeaveDays: Int = 0,
        /** Sick leave working days this month (not owed as work for the minimum wage). */
        val monthSickWorkdays: Int = 0,
        /** Annual leave hours this month (paid, counted for the minimum wage). */
        val monthVacationHours: Double = 0.0,
        /** Salary not paid for the days of illness. */
        val salaryReduction: Double = 0.0,
        /** Holiday pay of hourly pay (counted for the minimum wage). */
        val holidayPay: Double = 0.0,
        /** SH-223: hours of leave taken in part of a day in the settlement period (shorten its norm too). */
        val periodLeaveHours: Double = 0.0,
        /** SH-223: unpaid (and ZUS-paid) hours this month: not owed as work for the minimum wage. */
        val monthUnpaidHours: Double = 0.0,
        /** SH-223: hours of time off paid in full this month (counted for the minimum wage). */
        val monthPaidLeaveHours: Double = 0.0,
        /** SH-223: hourly pay for that time off (counted for the minimum wage). */
        val paidLeavePay: Double = 0.0,
        /** SH-223: salary not paid for unpaid and half-paid time off. */
        val unpaidReduction: Double = 0.0
    ) {
        companion object {
            val NONE = LeaveEffect()
        }
    }

    fun compensationOn(periods: List<RatePeriod>, fallback: CompensationModel, date: LocalDate): CompensationModel {
        if (periods.isEmpty()) return fallback
        val day = date.toString()
        val sorted = periods.sortedBy { it.effectiveFrom }
        return sorted.lastOrNull { it.effectiveFrom <= day }?.compensation ?: sorted.first().compensation
    }

    /** The stretches of [first]..[last] with the terms in force on each. */
    fun parts(periods: List<RatePeriod>, fallback: CompensationModel, first: LocalDate, last: LocalDate): List<RatePart> {
        val starts = periods.mapNotNull { runCatching { LocalDate.parse(it.effectiveFrom) }.getOrNull() }
            .filter { it > first && it <= last }
            .distinct().sorted()
        val bounds = listOf(first) + starts
        return bounds.mapIndexed { i, from ->
            val to = if (i + 1 < bounds.size) bounds[i + 1].minus(DatePeriod(days = 1)) else last
            RatePart(from, to, compensationOn(periods, fallback, from))
        }.fold(mutableListOf<RatePart>()) { out, part ->
            // Two stretches with the same terms are one.
            val previous = out.lastOrNull()
            if (previous != null && previous.compensation == part.compensation) out[out.lastIndex] = previous.copy(to = part.to) else out += part
            out
        }
    }

    fun monthStart(year: Int, month: Int) = LocalDate(year, month, 1)
    fun monthEnd(year: Int, month: Int): LocalDate = monthStart(year, month).plus(DatePeriod(months = 1)).minus(DatePeriod(days = 1))

    private fun hoursIn(spans: List<WorkedSpan>, from: LocalDate, to: LocalDate) =
        spans.filter { it.start.date in from..to }.sumOf { minutes(it) } / 60.0

    private fun minutes(span: WorkedSpan): Long =
        (span.end.toInstant(TimeZone.UTC) - span.start.toInstant(TimeZone.UTC)).inWholeMinutes

    /**
     * One set of terms standing for the month in the overtime and minimum-wage maths: the only one,
     * or for hourly pay the rate weighted by the hours worked under each, for a salary the pro-rata
     * salary; when hourly and salaried terms mix, the month-end terms.
     */
    fun blended(parts: List<RatePart>, spans: List<WorkedSpan>, year: Int, month: Int): CompensationModel {
        val last = parts.last().compensation
        if (parts.size == 1) return last
        val norm = PolishCalendar.workingHours(year, month).toDouble()
        return when {
            parts.all { it.compensation is CompensationModel.Hourly } -> {
                val weights = parts.map { hoursIn(spans, it.from, it.to) }.takeIf { w -> w.sum() > 0 }
                    ?: parts.map { PolishCalendar.workingHours(it.from, it.to).toDouble() }
                val total = weights.sum()
                val rate = if (total > 0) parts.indices.sumOf { (parts[it].compensation as CompensationModel.Hourly).rate * weights[it] } / total
                else (last as CompensationModel.Hourly).rate
                (last as CompensationModel.Hourly).copy(rate = rate)
            }
            parts.all { it.compensation is CompensationModel.MonthlyFixed } && norm > 0 ->
                (last as CompensationModel.MonthlyFixed).copy(baseSalary = proRataSalary(parts, norm))
            else -> last
        }
    }

    private fun proRataSalary(parts: List<RatePart>, norm: Double): Double =
        parts.sumOf { part ->
            val salary = (part.compensation as? CompensationModel.MonthlyFixed)?.baseSalary ?: 0.0
            salary * PolishCalendar.workingHours(part.from, part.to) / norm
        }

    /**
     * The month's base pay under [parts]: salaries pro rata to the norm's working hours, hourly
     * terms for the hours worked under them.
     */
    fun basePay(parts: List<RatePart>, spans: List<WorkedSpan>, year: Int, month: Int): Double {
        val norm = PolishCalendar.workingHours(year, month).toDouble()
        return parts.sumOf { part ->
            when (val pay = part.compensation) {
                is CompensationModel.Hourly -> pay.rate * hoursIn(spans, part.from, part.to)
                is CompensationModel.MonthlyFixed -> if (norm > 0) pay.baseSalary * PolishCalendar.workingHours(part.from, part.to) / norm else 0.0
            }
        }
    }

    /** What the pro-rata month differs from paying [monthEndTerms] all month by: salaries only (shifts are priced by date). */
    fun rateChangeAdjustment(parts: List<RatePart>, spans: List<WorkedSpan>, year: Int, month: Int, monthEndTerms: CompensationModel): Double {
        if (parts.size < 2 || monthEndTerms !is CompensationModel.MonthlyFixed) return 0.0
        return basePay(parts, spans, year, month) - monthEndTerms.baseSalary
    }

    fun isWorkingDay(date: LocalDate) =
        date.dayOfWeek != DayOfWeek.SATURDAY && date.dayOfWeek != DayOfWeek.SUNDAY && !PolishCalendar.isHoliday(date)

    /**
     * One entry per day; sick leave wins over annual leave on the same day (an illness interrupts
     * the leave), and a whole day over part of one (SH-223).
     */
    fun distinctDays(leave: List<LeaveDay>): List<LeaveDay> =
        leave.groupBy { it.date }.map { (_, days) ->
            days.firstOrNull { it.kind == LeaveKind.SICK_FULL_PAY } ?: days.firstOrNull { it.kind == LeaveKind.SICK }
                ?: days.firstOrNull { it.hours == null } ?: days.first()
        }.sortedBy { it.date }

    /** SH-223: the time off other than sick and annual leave. */
    private val OTHER_LEAVE = setOf(LeaveKind.PAID_LEAVE, LeaveKind.HALF_PAID_LEAVE, LeaveKind.UNPAID_LEAVE, LeaveKind.BENEFIT_LEAVE)

    /** One earlier month: its pay and hours, for the averages. */
    data class PastMonth(val year: Int, val month: Int, val hours: Double, val gross: Double, val variable: Double)

    /**
     * The months before [input]'s month (at most [months]) in which the person worked here, with
     * the pay worked out from their work and terms: base pay, night allowance and overtime.
     */
    fun history(input: MonthWorkInput, contract: SystemContractType, months: Int): List<PastMonth> {
        val periods = input.ratePeriods
        val employment = contract == SystemContractType.STANDARD_EMPLOYMENT
        return (1..months).mapNotNull { back ->
            val index = input.year * 12 + input.month - 1 - back
            val year = index / 12
            val month = index % 12 + 1
            val first = monthStart(year, month)
            val last = monthEnd(year, month)
            val spans = input.earlierWork.filter { it.start.date in first..last && it.end > it.start }
            if (spans.isEmpty()) return@mapNotNull null
            val parts = parts(periods, input.compensation, first, last)
            val terms = blended(parts, spans, year, month)
            val base = basePay(parts, spans, year, month)
            val norm = PolishCalendar.workingHours(year, month).toDouble()
            val monthRates = PolandRates.forYear(year)
            val night = if (employment && norm > 0) {
                spans.sumOf { PolandPayroll.nightHours(it.start, it.end, input.settings) } * monthRates.nightAllowanceRate * monthRates.minimumWage / norm
            } else 0.0
            val overtime = if (employment) {
                val work = PolishWorkingTime.workFactors(
                    MonthWorkInput(year, month, contract, terms, input.declaredHours, input.earlierWork, input.settings),
                    LeaveEffect.NONE
                )
                work.supplements + work.overtimePay
            } else 0.0
            PastMonth(year, month, spans.sumOf { minutes(it) } / 60.0, base + night + overtime, night + overtime)
        }
    }

    /**
     * Sick pay, the ZUS benefit, the salary reduction and holiday pay of the month, added to [work]
     * (the month's working-time factors).
     */
    fun leaveFactors(
        input: MonthWorkInput,
        contract: SystemContractType,
        parts: List<RatePart>,
        fte: Double,
        rates: PolandRates
    ): Pair<MonthPayFactors, LeaveEffect> {
        val empty = MonthPayFactors.none(input.year, input.month) to LeaveEffect.NONE
        val first = monthStart(input.year, input.month)
        val last = monthEnd(input.year, input.month)
        val days = distinctDays(input.leave.filter { it.date.year == input.year && it.date <= last })
        val employment = contract == SystemContractType.STANDARD_EMPLOYMENT
        // SH-149: a civil-law contract's sickness insurance as it was on each day (a student status that ended).
        fun civilInsuredOn(day: LocalDate): Boolean = contract == SystemContractType.CIVIL_LAW_CONTRACT &&
            input.profileOn(day).let { it.voluntarySickness && !it.student && !it.insuredElsewhereAtMinimumWage }
        val civilInsured = generateSequence(first) { it.plus(DatePeriod(days = 1)) }.takeWhile { it <= last }.any(::civilInsuredOn)
        if (!employment && !civilInsured) return empty

        val sickThisMonth = days.filter { it.kind.isSick && it.date >= first }
        val vacationThisMonth = if (employment) days.filter { it.kind == LeaveKind.VACATION && it.date >= first && isWorkingDay(it.date) } else emptyList()
        val entitlement = if (employment) ceil((if (input.profileOn(last).longService) rates.leaveDaysLongService else rates.leaveDays) * fte).toInt() else 0
        val used = if (employment) days.count { it.kind == LeaveKind.VACATION && isWorkingDay(it.date) } else 0
        // SH-223: the other time off on working days this month (Labour Code rights: employment only).
        val otherThisMonth = if (employment) days.filter { it.kind in OTHER_LEAVE && it.date >= first && isWorkingDay(it.date) } else emptyList()
        if (sickThisMonth.isEmpty() && vacationThisMonth.isEmpty() && otherThisMonth.isEmpty()) {
            return MonthPayFactors.none(input.year, input.month).copy(leaveEntitlementDays = entitlement, leaveUsedDays = used) to LeaveEffect.NONE
        }

        val monthSpans = input.shifts.filter { it.start.date in first..last && it.end > it.start }
        val history = history(input, contract, max(rates.sickBaseMonths, rates.holidayAverageMonths))

        // Sick pay: the base, then day by day against the year's employer days.
        var sickPay = 0.0
        var benefit = 0.0
        var sickPayDays = 0
        var benefitDays = 0
        var base = 0.0
        if (sickThisMonth.isNotEmpty()) {
            val pastYear = history.filter { (input.year * 12 + input.month) - (it.year * 12 + it.month) <= rates.sickBaseMonths }
            val gross = if (pastYear.isNotEmpty()) pastYear.sumOf { it.gross } / pastYear.size
            else when (val terms = input.compensation) {
                is CompensationModel.MonthlyFixed -> terms.baseSalary
                is CompensationModel.Hourly -> {
                    // No earlier month here: the stated hours, else this month's with the sick working days at 8 hours.
                    val hours = input.declaredHours?.toDouble()
                        ?: (monthSpans.sumOf { minutes(it) } / 60.0 + sickThisMonth.count { isWorkingDay(it.date) } * rates.leaveDayHours * fte)
                    terms.rate * hours
                }
            }
            base = gross * (1 - rates.employeeSocialRate)
            val daily = base / rates.sickDaysInBaseMonth
            var counted = days.count { it.kind.isSick && it.date < first }
            sickThisMonth.forEach { day ->
                // SH-149: insured on that day (civil-law), and the employer's days as on that day.
                if (!employment && !civilInsuredOn(day.date)) return@forEach
                val limit = if (!employment) 0 else if (input.profileOn(day.date).over50) rates.employerSickDaysOver50 else rates.employerSickDays
                val rate = if (day.kind == LeaveKind.SICK_FULL_PAY) rates.sickPayFullRate else rates.sickPayRate
                if (counted < limit) {
                    sickPay += daily * rate
                    sickPayDays++
                } else {
                    benefit += daily * rate
                    benefitDays++
                }
                counted++
            }
        }

        // A salary is not paid for the days of illness: 1/30 of the day's salary each, at most all of it.
        val salaryReduction = if (employment) {
            val byDay = sickThisMonth.sumOf { day ->
                (compensationOn(input.ratePeriods, input.compensation, day.date) as? CompensationModel.MonthlyFixed)?.baseSalary?.div(30) ?: 0.0
            }
            val monthSalary = parts.sumOf { part ->
                (part.compensation as? CompensationModel.MonthlyFixed)?.let { basePay(listOf(part), emptyList(), input.year, input.month) } ?: 0.0
            }
            val wholeMonth = sickThisMonth.size >= last.day
            if (wholeMonth) monthSalary else min(byDay, monthSalary)
        } else 0.0

        // Annual leave: 8 hours × FTE a working day; hourly terms pay them, a salary already does.
        val leaveHours = rates.leaveDayHours * fte
        val holidayPay = vacationThisMonth.sumOf { day ->
            (compensationOn(input.ratePeriods, input.compensation, day.date) as? CompensationModel.Hourly)?.rate?.times(leaveHours) ?: 0.0
        }
        val vacationHours = vacationThisMonth.size * leaveHours
        val recent = history.filter { (input.year * 12 + input.month) - (it.year * 12 + it.month) <= rates.holidayAverageMonths }
        val recentHours = recent.sumOf { it.hours }
        val variablePerHour = if (recentHours > 0) recent.sumOf { it.variable } / recentHours else 0.0
        val holidayVariable = variablePerHour * vacationHours

        // SH-223: other time off. Paid in full (blood donation, childcare, urlop okolicznościowy, time
        // off in lieu) or at half (force majeure): hourly pay pays its hours, a salary already does
        // (less half for the half-paid ones). Unpaid and ZUS-paid: a salary is reduced by its hourly
        // value (the salary over the month's own norm) for each hour; hourly pay pays nothing.
        val ownNorm = PolishCalendar.workingHours(input.year, input.month) * fte
        fun hoursOf(day: LeaveDay) = day.hours?.coerceIn(0.0, leaveHours) ?: leaveHours
        fun hourlyRate(date: LocalDate) = (compensationOn(input.ratePeriods, input.compensation, date) as? CompensationModel.Hourly)?.rate ?: 0.0
        fun salaryHour(date: LocalDate) = (compensationOn(input.ratePeriods, input.compensation, date) as? CompensationModel.MonthlyFixed)
            ?.baseSalary?.let { if (ownNorm > 0) it / ownNorm else 0.0 } ?: 0.0
        val paidOther = otherThisMonth.filter { it.kind == LeaveKind.PAID_LEAVE }
        val halfOther = otherThisMonth.filter { it.kind == LeaveKind.HALF_PAID_LEAVE }
        val unpaidOther = otherThisMonth.filter { it.kind == LeaveKind.UNPAID_LEAVE || it.kind == LeaveKind.BENEFIT_LEAVE }
        val paidLeavePay = paidOther.sumOf { hourlyRate(it.date) * hoursOf(it) } + halfOther.sumOf { hourlyRate(it.date) * hoursOf(it) * rates.forceMajeurePayRate }
        val monthSalaryLeft = parts.sumOf { part ->
            (part.compensation as? CompensationModel.MonthlyFixed)?.let { basePay(listOf(part), emptyList(), input.year, input.month) } ?: 0.0
        } - salaryReduction
        val unpaidReduction = min(
            max(0.0, monthSalaryLeft),
            unpaidOther.sumOf { salaryHour(it.date) * hoursOf(it) } + halfOther.sumOf { salaryHour(it.date) * hoursOf(it) * (1 - rates.forceMajeurePayRate) }
        )

        // Leave days of the settlement period shorten its norm (SH-223: part of a day by its hours).
        val periodFirst = monthStart(input.year, input.settings.settlementMonths(input.month).first)
        val periodLeaveDays = if (employment) days.count { it.date >= periodFirst && isWorkingDay(it.date) && it.hours == null } else 0
        val periodLeaveHours = if (employment) days.filter { it.date >= periodFirst && isWorkingDay(it.date) && it.hours != null }.sumOf { hoursOf(it) } else 0.0

        val factors = MonthPayFactors.none(input.year, input.month).copy(
            sickDays = sickThisMonth.size,
            sickPayDays = sickPayDays,
            sickPay = PolandPayroll.money(sickPay),
            sickBenefitDays = benefitDays,
            sickBenefit = PolandPayroll.money(benefit),
            sickPayBase = PolandPayroll.money(base),
            salaryReduction = PolandPayroll.money(salaryReduction),
            vacationDays = vacationThisMonth.size,
            vacationHours = vacationHours,
            holidayPay = PolandPayroll.money(holidayPay),
            holidayVariablePay = PolandPayroll.money(holidayVariable),
            leaveEntitlementDays = entitlement,
            leaveUsedDays = used,
            paidLeaveDays = paidOther.size,
            paidLeaveHours = paidOther.sumOf { hoursOf(it) },
            halfPaidLeaveDays = halfOther.size,
            halfPaidLeaveHours = halfOther.sumOf { hoursOf(it) },
            paidLeavePay = PolandPayroll.money(paidLeavePay),
            unpaidLeaveDays = unpaidOther.count { it.kind == LeaveKind.UNPAID_LEAVE },
            benefitLeaveDays = unpaidOther.count { it.kind == LeaveKind.BENEFIT_LEAVE },
            unpaidLeaveReduction = PolandPayroll.money(unpaidReduction)
        )
        val effect = LeaveEffect(
            periodLeaveDays = periodLeaveDays,
            monthSickWorkdays = if (employment) sickThisMonth.count { isWorkingDay(it.date) } else 0,
            monthVacationHours = vacationHours,
            salaryReduction = salaryReduction,
            holidayPay = holidayPay,
            periodLeaveHours = periodLeaveHours,
            monthUnpaidHours = unpaidOther.sumOf { hoursOf(it) },
            monthPaidLeaveHours = paidOther.sumOf { hoursOf(it) },
            paidLeavePay = paidLeavePay,
            unpaidReduction = unpaidReduction
        )
        return factors to effect
    }
}
